// Identity handshake with the AI Quiz Supporter embed: it posts REQUEST on mount and falls back to a shared dev teacher after 1.5s without an answer.
export const TEACHER_INFO_REQUEST = 'izteach:request-teacher-info';
export const TEACHER_INFO = 'izteach:teacher-info';

// The embed rejects the payload unless all four are strings.
export interface EmbedTeacherInfo {
  teacherId: string;
  teacherEmail: string;
  teacherName: string;
  vendorId: string;
}

export interface TeacherInfoMessage {
  type: typeof TEACHER_INFO;
  payload: EmbedTeacherInfo;
}

export interface TeacherIdentitySources {
  iz: {
    kind: 'teacher' | 'student' | null;
    profileId: string | null;
    userId: string | null;
    vendorId: string;
    email: string | null;
  };
  qlt: {
    userId: string | number | null | undefined;
    teacherName: string | null | undefined;
    email: string | null | undefined;
  };
  /** the IZ vendor the site is built for (VITE_IZT_VENDOR_ID) */
  defaultVendorId: string;
}

const DEFAULT_TEACHER_NAME = 'Giáo viên';

function text(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

/**
 * The IZ teacher id when an IZ teacher session exists, else `qlt:<QLT user id>`; null with no signed-in teacher.
 * The prefix keeps QLT numeric ids from colliding with IZ ids under the same vendor.
 */
export function buildTeacherInfo({ iz, qlt, defaultVendorId }: TeacherIdentitySources): EmbedTeacherInfo | null {
  const teacherName = text(qlt.teacherName) || DEFAULT_TEACHER_NAME;
  const izId = iz.kind === 'teacher' ? text(iz.profileId) || text(iz.userId) : '';
  const izVendor = text(iz.vendorId) || text(defaultVendorId);
  if (izId && izVendor) {
    return { teacherId: izId, teacherEmail: text(iz.email) || text(qlt.email), teacherName, vendorId: izVendor };
  }
  const qltId = text(qlt.userId);
  const vendorId = text(defaultVendorId);
  if (!qltId || !vendorId) return null;
  return { teacherId: `qlt:${qltId}`, teacherEmail: text(qlt.email), teacherName, vendorId };
}

export function teacherInfoMessage(info: EmbedTeacherInfo): TeacherInfoMessage {
  return { type: TEACHER_INFO, payload: { ...info } };
}

export function isTeacherInfoRequest(data: unknown): boolean {
  return typeof data === 'object' && data !== null && (data as { type?: unknown }).type === TEACHER_INFO_REQUEST;
}

// Only the iframe we rendered, served from the origin we post to, may ask for the teacher's identity.
export function isFromEmbed(
  event: { origin: string; source: unknown },
  embedWindow: unknown,
  embedOrigin: string
): boolean {
  return Boolean(embedWindow) && event.source === embedWindow && Boolean(embedOrigin) && event.origin === embedOrigin;
}
